import { contactUsLinks, contactUsRoutes } from '@/backend/config/routes';
import { HeaderItem } from '@juvare/common-web-ui/GlobalApp';
import { PageHeaderWithImage } from '@juvare/common-web-ui/PageHeader';
import { ContactUsPanel, afterSubmitDialog } from '@juvare/contact-management/ui/features/contact-us';
import { useContactManagementTranslation } from '@juvare/contact-management/ui/hooks/useContactManagementTranslation';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { Settings } from '@juvare/platform-sdk-react/types';
import { EditablePageContent } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteObject } from 'react-router';
import { appName } from '../utils/utils';

interface Result {
    supportCenterRoutes: RouteObject[];
    supportCenterHeaderItem: HeaderItem[];
}

export function useSupportCenterMenu(pages: EditablePageData[]): Result {
    const { t } = useTranslation<'healthAlerts'>();
    const { t: tContactManagement } = useContactManagementTranslation();
    const settings = useSelector(getSettings);
    const supportCenterPages = pages.filter((page) => page.tags.includes('supportCenter'));
    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        supportCenterRoutes: createRoute(supportCenterPages, tContactManagement as any, settings),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        supportCenterHeaderItem: createHeaderItem(supportCenterPages, settings.helpUrl, t, tContactManagement as any, settings),
    };
}

function createRoute(pages: EditablePageData[], t: TFunction, settings: Settings): RouteObject[] {
    const routes = [
        {
            path: 'support',
            children: pages.map((page) => ({
                path: page.url,
                element: (
                    <>
                        <PageHeaderWithImage title={page.title ?? page.label} gradient='blue-base' />
                        <Box
                            sx={{
                                px: { xs: 2, sm: 4, xl: 5 },
                                py: 4,
                            }}
                            data-test='page-content'
                        >
                            <EditablePageContent page={page} />
                        </Box>
                    </>
                ),
            })),
        },
    ];

    const contactUsRoute = [];
    if (settings.enableContactUs.enabled) {
        contactUsRoute.push({
            path: contactUsRoutes.root,
            element: (
                <Box>
                    <PageHeaderWithImage gradient='blue-dark' title={t('contactUs.title')} />
                    <Box sx={{ maxWidth: 640, mx: 8, mt: 8 }}>
                        <ContactUsPanel
                            appName={appName}
                            isAnonymous={false}
                            resetFormAfterSubmit
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onAfterSubmit={(data) => afterSubmitDialog(t as any, data)}
                        />
                    </Box>
                </Box>
            ),
        });
    }
    return [...routes, ...contactUsRoute];
}

function createHeaderItem(
    pages: EditablePageData[],
    helpUrl: string,
    t: TFunction<'healthAlerts'>,
    tContactManagement: TFunction,
    settings: Settings,
): HeaderItem[] {
    const menuItems = [
        ...pages.map((page) => ({
            label: page.label,
            navigateTo: '/support/' + page.url,
        })),
        {
            label: t('appHeader.helpCenter.help'),
            onClick: () => window.open(helpUrl, '_blank'),
            'data-test': 'toolbar-help-item',
        },
    ];

    if (settings.enableContactUs.enabled) {
        menuItems.push({
            label: tContactManagement('contactUs.title'),
            navigateTo: contactUsLinks.root,
            'data-test': 'toolbar-contact-us-item',
        });
    }
    return [
        {
            icon: <HelpOutlineIcon />,
            label: t('appHeader.helpCenter.help'),
            'data-test': 'help-menu-button',
            menuItems,
        },
    ];
}
